import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Lock, Wallet, Hourglass, XCircle, Zap, Banknote, Car, CheckCircle2, Send,
  Download, Filter, ChevronRight, RefreshCcw, Pause, RotateCw, FileDown, CircleDollarSign,
} from "lucide-react";
import { adminApi } from "./api";
import { toast } from "sonner";
import { PageHeader } from "./shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PayoutDrawer, RefundDialog, PayoutsAllDialog, EtransferAllDialog, PendingTripsAllDialog, AwaitingApprovalDialog, SettleRideDialog, DisputeDialog } from "./FinanceDialogs";

const money = (n) => `$${(n ?? 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PERIODS = [["7d", "Last 7 days"], ["14d", "Last 14 days"], ["30d", "Last 30 days"]];

const CARD_DEFS = [
  { key: "escrow", label: "Total Money Held in Escrow", icon: Lock, color: "#16a34a", tint: "#16a34a",
    val: (c) => money(c.escrow.amount), sub: "From riders for ongoing trips" },
  { key: "ready_for_payout", label: "Ready for Payout", icon: Wallet, color: "#2340b5", tint: "#2340b5",
    val: (c) => money(c.ready_for_payout.amount), sub: "Completed trips awaiting payout" },
  { key: "pending_trips", label: "Pending Trips", icon: Hourglass, color: "#f59e0b", tint: "#f59e0b",
    val: (c) => c.pending_trips, sub: "Trips not completed yet" },
  { key: "cancelled_bookings", label: "Cancelled Bookings", icon: XCircle, color: "#e4002b", tint: "#e4002b",
    val: (c) => c.cancelled_bookings, sub: "Refunds & cancellations" },
  { key: "etransfer_queue", label: "Instant E-Transfer Queue", icon: Zap, color: "#7c3aed", tint: "#7c3aed",
    val: (c) => c.etransfer_queue, sub: "Drivers requesting payout" },
];

const FLOW = [
  { icon: CircleDollarSign, title: "Rider Books Trip", sub: "Payment authorized", color: "#16a34a" },
  { icon: Lock, title: "Funds Held Securely", sub: "In escrow", color: "#f59e0b" },
  { icon: Car, title: "Trip in Progress", sub: "Driver accepted & trip started", color: "#2340b5" },
  { icon: CheckCircle2, title: "Trip Completed", sub: "Rider & driver completed", color: "#2340b5" },
  { icon: Wallet, title: "Ready for Payout", sub: "Eligible for driver payout", color: "#f59e0b" },
  { icon: Send, title: "Payout Sent", sub: "E-transfer / Bank deposit", color: "#7c3aed" },
];

const QUICK = [
  { action: "approve-payout", label: "Approve Payout", icon: CheckCircle2, color: "#16a34a" },
  { action: "send-etransfer", label: "Send E-Transfer", icon: Send, color: "#2340b5" },
  { action: "process-refund", label: "Process Refund", icon: RotateCw, color: "#f59e0b" },
  { action: "hold-payment", label: "Hold Payment", icon: Pause, color: "#f59e0b" },
  { action: "retry-payment", label: "Retry Payment", icon: RefreshCcw, color: "#2340b5" },
  { action: "export-report", label: "Export Report", icon: FileDown, color: "#7c3aed" },
];

const Card = ({ title, right, children, className = "" }) => (
  <div className={`rounded-2xl border border-[#12142b]/8 bg-white p-6 shadow-sm ${className}`}>
    {(title || right) && (
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-lg font-bold text-[#12142b]">{title}</h2>
        {right}
      </div>
    )}
    {children}
  </div>
);

const Avatar = ({ name }) => (
  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#2340b5]/10 text-xs font-bold text-[#2340b5]">
    {(name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
  </span>
);

const HOLD_HOURS = 12;
const PayoutCountdown = ({ completedAt }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  if (!completedAt) return null;
  const done = new Date(completedAt).getTime();
  if (Number.isNaN(done)) return null;
  const eligibleAt = done + HOLD_HOURS * 3600 * 1000;
  const remaining = eligibleAt - now;
  if (remaining <= 0) {
    return (
      <span data-testid="payout-countdown-ready" className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-[#16a34a]">
        <CheckCircle2 className="h-3 w-3" /> Ready for payout
      </span>
    );
  }
  const h = Math.floor(remaining / 3600000);
  const m = Math.floor((remaining % 3600000) / 60000);
  return (
    <span
      data-testid="payout-countdown"
      title={`Auto-payout ${new Date(eligibleAt).toLocaleString("en-CA")}`}
      className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-[#b45309]"
    >
      <Hourglass className="h-3 w-3" /> Auto-payout in {h}h {m}m
    </span>
  );
};

const TXN_TYPE_COLOR = (t) =>
  /refund/i.test(t) ? "#e4002b" : /payout|transfer/i.test(t) ? "#16a34a" : /booking/i.test(t) ? "#2340b5" : "#7c3aed";

const TXN_STATUS_STYLE = (s) => {
  const st = (s || "").toLowerCase();
  if (["completed", "paid", "paid_out", "succeeded", "released"].includes(st)) return ["#16a34a", "#16a34a14"];
  if (["pending", "processing", "in_transit", "awaiting"].includes(st)) return ["#b45309", "#f59e0b1f"];
  return ["#565b73", "#565b7314"];
};

const fmtTxnTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const DRow = ({ l, v }) => (
  <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
    <span className="shrink-0 text-[#565b73]">{l}</span>
    <span className="text-right font-semibold text-[#12142b]">{v || "—"}</span>
  </div>
);

function TxnDetailDialog({ txnId, onClose }) {
  const [detail, setDetail] = useState(undefined); // undefined=loading, null=not found
  useEffect(() => {
    if (!txnId) return;
    setDetail(undefined);
    adminApi.get(`/finance/transactions/${txnId}`)
      .then((r) => setDetail(r.data))
      .catch(() => setDetail(null));
  }, [txnId]);

  const t = detail?.transaction;
  const b = detail?.booking;
  const p = detail?.payout;
  const r = detail?.refund;
  const Section = ({ title, children, empty }) => (
    <div className="rounded-xl border border-[#12142b]/8 p-4">
      <p className="mb-1 text-xs font-bold uppercase tracking-wide text-[#565b73]">{title}</p>
      {children || <p className="py-1 text-sm text-[#565b73]">{empty}</p>}
    </div>
  );

  return (
    <Dialog open={!!txnId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="txn-detail-dialog">
        <DialogHeader><DialogTitle>Transaction detail</DialogTitle></DialogHeader>
        {detail === undefined ? (
          <p className="py-10 text-center text-sm text-[#565b73]" data-testid="txn-detail-loading">Loading transaction…</p>
        ) : detail === null || !t ? (
          <p className="py-10 text-center text-sm text-[#565b73]" data-testid="txn-detail-missing">This transaction is no longer available in the live feed.</p>
        ) : (
          <div className="space-y-3" data-testid="txn-detail-body">
            <Section title={`Transaction ${t.short_id || ""}`}>
              <DRow l="Type" v={t.type} />
              <DRow l="From → To" v={`${t.from_name} → ${t.to_name}`} />
              <DRow l="Amount" v={money(t.amount)} />
              <DRow l="Status" v={t.status} />
              <DRow l="Method" v={t.card_last4 ? `${t.card_brand} ••${t.card_last4}` : "Wallet / other"} />
              <DRow l="Date" v={fmtTxnTime(t.created_at)} />
            </Section>
            <Section title="Booking" empty="No booking record linked to this transaction.">
              {b && (<>
                <DRow l="Route" v={b.route} />
                <DRow l="Rider" v={b.rider} />
                <DRow l="Driver" v={b.driver} />
                <DRow l="Booking total" v={money(b.total)} />
                <DRow l="Driver earnings" v={money(b.amount)} />
                <DRow l="Payment method" v={b.payment_method} />
                <DRow l="Status" v={b.status} />
                <DRow l="Completed" v={fmtTxnTime(b.completed_at)} />
                {b.payout_hold && <DRow l="Payout hold" v={b.payout_hold_reason || "On hold"} />}
                {b.has_dispute && <DRow l="Dispute" v="Open dispute on this booking" />}
              </>)}
            </Section>
            <Section title="Driver payout" empty="No payout recorded for this transaction's driver yet.">
              {p && (<>
                <DRow l="Status" v={p.status} />
                <DRow l="Amount" v={money(p.amount)} />
                {p.fee > 0 && <DRow l="Fast-cash fee" v={money(p.fee)} />}
                <DRow l="Type" v={p.instant ? "Instant (e-transfer)" : "Regular"} />
                <DRow l="Destination" v={p.destination ? `••${p.destination}` : ""} />
                <DRow l="Requested" v={fmtTxnTime(p.created_at)} />
              </>)}
            </Section>
            <Section title="Refund" empty="No refund linked to this transaction.">
              {r && (<>
                <DRow l="Status" v={r.status || (r.processed ? "processed" : "pending")} />
                <DRow l="Refund amount" v={money(r.amount)} />
                <DRow l="Original charge" v={money(r.total)} />
                <DRow l="Reason" v={r.reason} />
                <DRow l="Cancelled" v={fmtTxnTime(r.at)} />
              </>)}
            </Section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RecentTransactionsCard({ period }) {
  const [txns, setTxns] = useState(null);
  const [sel, setSel] = useState(null);
  useEffect(() => {
    setTxns(null);
    adminApi.get("/finance/profit-breakdown", { params: { period } })
      .then((r) => {
        const items = (r.data.transactions || []).slice()
          .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        // Skip $0 wallet-charge noise — staff need real money movement.
        const meaningful = items.filter((t) => (t.amount || 0) > 0);
        setTxns((meaningful.length ? meaningful : items).slice(0, 6));
      })
      .catch(() => setTxns([]));
  }, [period]);

  return (
    <Card title="Recent Transactions">
      <div data-testid="recent-transactions-card">
        {txns === null ? (
          <p className="py-8 text-center text-sm text-[#565b73]">Loading transactions…</p>
        ) : txns.length === 0 ? (
          <p className="py-8 text-center text-sm text-[#565b73]" data-testid="recent-transactions-empty">No transactions in this period.</p>
        ) : (
          txns.map((t) => {
            const color = TXN_TYPE_COLOR(t.type);
            const [sc, sbg] = TXN_STATUS_STYLE(t.status);
            return (
              <button key={t.id} type="button" data-testid={`recent-txn-row-${t.id}`} onClick={() => setSel(t.id)}
                className="flex w-full items-center gap-3 border-b border-[#12142b]/6 py-2.5 text-left transition-colors last:border-0 hover:bg-[#f4f6fb]/70">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-xs font-black" style={{ backgroundColor: `${color}14`, color }}>
                  {(t.type || "?").slice(0, 1)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-[#12142b]">{t.type} · {t.from_name} → {t.to_name}</p>
                  <p className="mt-0.5 flex items-center gap-2 text-xs text-[#565b73]">
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-bold capitalize" style={{ color: sc, backgroundColor: sbg }}>{t.status}</span>
                    {t.short_id && <span className="font-mono font-semibold">{t.short_id}</span>}
                    {fmtTxnTime(t.created_at)}
                    {t.card_last4 ? `· ${t.card_brand} ••${t.card_last4}` : ""}
                  </p>
                </div>
                <span className="shrink-0 text-sm font-bold text-[#12142b]">{money(t.amount)}</span>
                <ChevronRight className="h-4 w-4 shrink-0 text-[#565b73]/60" />
              </button>
            );
          })
        )}
      </div>
      <TxnDetailDialog txnId={sel} onClose={() => setSel(null)} />
    </Card>
  );
}

export default function PaymentsFinance() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("7d");
  const [data, setData] = useState(null);
  const [refunds, setRefunds] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [rpage, setRpage] = useState(1);
  const [err, setErr] = useState(false);
  const [payout, setPayout] = useState(null);
  const [refund, setRefund] = useState(null);
  const [showAllPayouts, setShowAllPayouts] = useState(false);
  const [showEtransfer, setShowEtransfer] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const [awaiting, setAwaiting] = useState({ items: [], total: 0 });
  const [showAwaiting, setShowAwaiting] = useState(false);
  const [settleTrip, setSettleTrip] = useState(null);
  const [disputeTrip, setDisputeTrip] = useState(null);

  const firstFin = useRef(true);
  const prevCounts = useRef({ queue: 0, ready: 0 });
  const firstRef = useRef(true);
  const prevRefundTotal = useRef(0);

  const load = useCallback(() => {
    adminApi.get("/finance/overview", { params: { period } })
      .then((r) => {
        const d = r.data;
        const q = d.cards.etransfer_queue || 0;
        const rp = d.cards.ready_for_payout?.count || 0;
        if (!firstFin.current) {
          const dq = q - prevCounts.current.queue;
          const dr = rp - prevCounts.current.ready;
          if (dq > 0) toast.info(`${dq} new instant payout request${dq > 1 ? "s" : ""} in queue`, { description: "Check the E-Transfer queue" });
          if (dr > 0) toast.info(`${dr} new payout${dr > 1 ? "s" : ""} ready`, { description: "Completed trips awaiting payout" });
        }
        prevCounts.current = { queue: q, ready: rp };
        firstFin.current = false;
        setData(d); setErr(false);
      })
      .catch(() => setErr(true));
  }, [period]);
  const loadRefunds = useCallback(() => {
    adminApi.get("/finance/refunds", { params: { page: rpage } }).then((r) => {
      const total = r.data.total || 0;
      if (!firstRef.current && total > prevRefundTotal.current) {
        const dn = total - prevRefundTotal.current;
        toast.info(`${dn} new refund${dn > 1 ? "s" : ""} to review`, { description: "Cancelled bookings & refunds" });
      }
      prevRefundTotal.current = total;
      firstRef.current = false;
      setRefunds(r.data);
    });
  }, [rpage]);

  useEffect(() => { firstFin.current = true; prevCounts.current = { queue: 0, ready: 0 }; }, [period]);
  useEffect(() => { firstRef.current = true; }, [rpage]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRefunds(); }, [loadRefunds]);
  const loadAwaiting = useCallback(() => {
    adminApi.get("/finance/awaiting-approval")
      .then((r) => setAwaiting(r.data || { items: [], total: 0 }))
      .catch(() => setAwaiting({ items: [], total: 0 }));
  }, []);
  useEffect(() => { loadAwaiting(); }, [loadAwaiting]);
  useEffect(() => {
    const id = setInterval(() => { load(); loadRefunds(); }, 30000);
    return () => clearInterval(id);
  }, [load, loadRefunds]);

  const quick = async (action) => {
    try {
      const { data } = await adminApi.post("/finance/action", { action });
      toast.success(data.message);
      load();
    } catch { toast.error("Action failed"); }
  };

  if (err) return (
    <div className="grid min-h-screen place-items-center text-center">
      <div>
        <p className="text-[#e4002b]">Couldn&apos;t load finance data.</p>
        <button onClick={load} className="mt-3 rounded-xl bg-[#2340b5] px-5 py-2.5 text-sm font-bold text-white">Retry</button>
      </div>
    </div>
  );
  if (!data) return <div className="grid min-h-screen place-items-center text-[#565b73]">Loading finance…</div>;
  const de = data.driver_earnings;

  return (
    <div className="min-h-screen">
      <PageHeader
        title="Payments & Finance"
        right={
          <div className="flex items-center gap-2">
            <div className="flex rounded-xl border border-[#12142b]/12 bg-white p-1">
              {PERIODS.map(([v, l]) => (
                <button key={v} data-testid={`period-${v}`} onClick={() => setPeriod(v)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${period === v ? "bg-[#2340b5] text-white" : "text-[#565b73]"}`}>
                  {l}
                </button>
              ))}
            </div>
            <button className="flex items-center gap-2 rounded-xl border border-[#12142b]/12 bg-white px-4 py-2.5 text-sm font-semibold text-[#12142b] hover:bg-[#f4f6fb]"><Filter className="h-4 w-4" /> Filters</button>
            <button data-testid="finance-export" onClick={() => quick("export-report")} className="flex items-center gap-2 rounded-xl bg-[#2340b5] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1b3390]"><Download className="h-4 w-4" /> Export</button>
          </div>
        }
      />

      <div className="space-y-6 p-8">
        {/* Top cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          {CARD_DEFS.map((c) => (
            <div key={c.key} data-testid={`fcard-${c.key}`} className="rounded-2xl border p-5 shadow-sm" style={{ borderColor: `${c.tint}25`, backgroundColor: `${c.tint}0a` }}>
              <div className="flex items-start gap-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl" style={{ backgroundColor: `${c.color}18` }}>
                  <c.icon className="h-5 w-5" strokeWidth={1.75} style={{ color: c.color }} />
                </span>
                <div>
                  <p className="text-xs font-semibold text-[#565b73]">{c.label}</p>
                  <p className="mt-1 font-display text-2xl font-black text-[#12142b]">{c.val(data.cards)}</p>
                  <p className="mt-0.5 text-xs text-[#565b73]/80">{c.sub}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Flow + Recent Transactions */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Card title="Payment Process Flow" className="lg:col-span-2">
            <div className="flex items-start justify-between gap-1 overflow-x-auto">
              {FLOW.map((s, i) => (
                <div key={i} className="flex items-start">
                  <div className="flex w-24 flex-col items-center text-center">
                    <span className="grid h-14 w-14 place-items-center rounded-full" style={{ backgroundColor: `${s.color}18` }}>
                      <s.icon className="h-6 w-6" strokeWidth={1.75} style={{ color: s.color }} />
                    </span>
                    <p className="mt-3 text-xs font-bold text-[#12142b]">{s.title}</p>
                    <p className="mt-0.5 text-[10px] leading-tight text-[#565b73]">{s.sub}</p>
                  </div>
                  {i < FLOW.length - 1 && <ChevronRight className="mt-4 h-5 w-5 shrink-0 text-[#c3cbe4]" />}
                </div>
              ))}
            </div>
          </Card>
          <RecentTransactionsCard period={period} />
        </div>

        {/* Pending / Ready / lists + right column */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Card title="Pending Trips" right={<button data-testid="pending-view-all" onClick={() => setShowPending(true)} className="text-sm font-semibold text-[#2340b5] hover:underline">View All</button>}>
                <p className="-mt-3 mb-3 text-xs text-[#565b73]">Funds Held in Escrow</p>
                <table className="w-full text-left text-sm">
                  <thead><tr className="border-b border-[#12142b]/8 text-[10px] uppercase tracking-wide text-[#565b73]"><th className="pb-2">Driver</th><th className="pb-2">Route</th><th className="pb-2">Riders</th><th className="pb-2 text-right">Amount</th></tr></thead>
                  <tbody>
                    {data.pending_trips.map((t) => (
                      <tr key={t.id} className="border-b border-[#12142b]/5 last:border-0">
                        <td className="py-2.5"><div className="flex items-center gap-2"><Avatar name={t.driver} /><span className="truncate font-medium text-[#12142b]">{t.driver}</span></div></td>
                        <td className="py-2.5 text-[#565b73]">{t.route}</td>
                        <td className="py-2.5 text-[#565b73]">{t.riders}</td>
                        <td className="py-2.5 text-right font-semibold text-[#12142b]">{money(t.amount)}</td>
                      </tr>
                    ))}
                    {data.pending_trips.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-[#565b73]">No pending trips</td></tr>}
                  </tbody>
                </table>
              </Card>

              <Card title="Completed Trips – Ready for Payout" right={<button data-testid="completed-view-all" onClick={() => setShowAllPayouts(true)} className="text-sm font-semibold text-[#2340b5] hover:underline">View All</button>}>
                {data.ready_payout.length ? (
                  <table className="w-full text-left text-sm">
                    <thead><tr className="border-b border-[#12142b]/8 text-[10px] uppercase tracking-wide text-[#565b73]"><th className="pb-2">Driver</th><th className="pb-2">Status</th><th className="pb-2">Amount</th><th className="pb-2 text-right">Method</th></tr></thead>
                    <tbody>
                      {data.ready_payout.map((p) => {
                        const isPayout = !!p.id;
                        const canSettle = !isPayout && p.ride_id && !p.payout_hold;
                        return (
                        <tr
                          key={p.id || `earn-${p.booking_id || p.driver_id}`}
                          data-testid={isPayout ? `payout-row-${p.id}` : `earnings-row-${p.driver_id}`}
                          onClick={() => (isPayout ? setPayout(p) : (canSettle && setSettleTrip(p)))}
                          title={canSettle ? "Settle & pay driver" : undefined}
                          className={`border-b border-[#12142b]/5 last:border-0 ${(isPayout || canSettle) ? "cursor-pointer hover:bg-[#f9fafd]" : "cursor-default"}`}
                        >
                          <td className="py-2.5"><div className="flex items-center gap-2"><Avatar name={p.driver} /><span className="truncate font-medium text-[#12142b]">{p.driver}</span>{p.stripe_connected && <span data-testid={`stripe-badge-${p.booking_id || p.id}`} title="Stripe connected — pays out to their Stripe account" className="rounded-full bg-[#635bff]/12 px-1.5 py-0.5 text-[10px] font-bold text-[#635bff]">Stripe</span>}</div></td>
                          <td className="py-2.5">
                            {p.status === "awaiting" ? (
                              <div>
                                {p.payout_hold ? (
                                  <span className="rounded-full bg-[#7c3aed]/12 px-2 py-0.5 text-xs font-semibold text-[#7c3aed]" title={p.payout_hold_reason || "Payout on hold"}>On hold</span>
                                ) : (
                                  <>
                                    <span className="rounded-full bg-[#94a3b8]/18 px-2 py-0.5 text-xs font-semibold text-[#475569]" title="Driver withdraws this from the app">Awaiting request</span>
                                    <PayoutCountdown completedAt={p.completed_at || p.ready_at} />
                                  </>
                                )}
                                {p.report_id && (
                                  <button
                                    data-testid={`view-dispute-${p.booking_id}`}
                                    onClick={(e) => { e.stopPropagation(); navigate(`/admin/m/safety-center?report=${p.report_id}`); }}
                                    className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-[#2340b5] hover:underline"
                                    title="Open the linked safety report in the Safety Center"
                                  ><ChevronRight className="h-3 w-3" /> View Dispute</button>
                                )}
                                {!p.report_id && p.ticket_id && (
                                  <button
                                    data-testid={`view-dispute-${p.booking_id}`}
                                    onClick={(e) => { e.stopPropagation(); navigate(`/admin/support?ticket=${p.ticket_id}`); }}
                                    className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-[#2340b5] hover:underline"
                                    title="Open the linked support ticket in Customer Support"
                                  ><ChevronRight className="h-3 w-3" /> View Dispute</button>
                                )}
                                {p.has_dispute && !p.report_id && !p.ticket_id && (
                                  <button
                                    data-testid={`dispute-open-${p.booking_id}`}
                                    onClick={(e) => { e.stopPropagation(); setDisputeTrip(p); }}
                                    className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-[#e4002b] hover:underline"
                                    title="View dispute details and resolve"
                                  ><XCircle className="h-3 w-3" /> Dispute open — review →</button>
                                )}
                              </div>
                            ) : (
                              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${p.status === "hold" ? "bg-[#7c3aed]/12 text-[#7c3aed]" : "bg-[#f59e0b]/14 text-[#b45309]"}`}>{p.status === "hold" ? "Processing" : "Pending"}</span>
                            )}
                          </td>
                          <td className="py-2.5 font-semibold text-[#12142b]">{money(p.amount)}</td>
                          <td className="py-2.5 text-right text-[#565b73]">{p.method}{p.last4 ? ` ••${p.last4}` : ""}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : <p className="py-10 text-center text-sm text-[#565b73]">No trips ready for payout</p>}
              </Card>
            </div>

            <Card
              title="Awaiting Driver Approval"
              right={<button data-testid="awaiting-view-all" onClick={() => setShowAwaiting(true)} className="text-sm font-semibold text-[#2340b5] hover:underline">View All{awaiting.total ? ` (${awaiting.total})` : ""}</button>}
            >
              <p className="-mt-3 mb-3 text-xs text-[#565b73]">Booked but not yet approved by the driver · no funds escrowed until approval</p>
              {awaiting.items.length ? (
                <table className="w-full text-left text-sm" data-testid="awaiting-card-table">
                  <thead><tr className="border-b border-[#12142b]/8 text-[10px] uppercase tracking-wide text-[#565b73]"><th className="pb-2">Rider</th><th className="pb-2">Driver</th><th className="pb-2">Route</th><th className="pb-2 text-right">Amount</th></tr></thead>
                  <tbody>
                    {awaiting.items.slice(0, 6).map((t) => (
                      <tr key={t.id} data-testid={`awaiting-card-row-${t.id}`} className="border-b border-[#12142b]/5 last:border-0">
                        <td className="py-2.5"><div className="flex items-center gap-2"><Avatar name={t.rider} /><span className="truncate font-medium text-[#12142b]">{t.rider}</span></div></td>
                        <td className="py-2.5 text-[#565b73]">{t.driver}</td>
                        <td className="py-2.5 text-[#565b73]">{t.route}</td>
                        <td className="py-2.5 text-right font-semibold text-[#12142b]">{money(t.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="py-8 text-center text-sm text-[#565b73]">No bookings awaiting approval</p>}
            </Card>

            <Card title="Instant E-Transfer Queue" right={<button data-testid="etransfer-view-all" onClick={() => setShowEtransfer(true)} className="text-sm font-semibold text-[#2340b5] hover:underline">View All</button>}>
              {data.etransfer_queue.length ? (
                <table className="w-full text-left text-sm">
                  <thead><tr className="border-b border-[#12142b]/8 text-[10px] uppercase tracking-wide text-[#565b73]"><th className="pb-2">Driver</th><th className="pb-2">Amount</th><th className="pb-2">Request Time</th><th className="pb-2 text-right">Status</th></tr></thead>
                  <tbody>
                    {data.etransfer_queue.map((e) => (
                      <tr key={e.id} className="border-b border-[#12142b]/5 last:border-0">
                        <td className="py-2.5"><div className="flex items-center gap-2"><Avatar name={e.driver} /><span className="font-medium text-[#12142b]">{e.driver}</span></div></td>
                        <td className="py-2.5 font-semibold text-[#12142b]">{money(e.amount)}</td>
                        <td className="py-2.5 text-[#565b73]">{new Date(e.request_time).toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" })}</td>
                        <td className="py-2.5 text-right"><span className="rounded-full bg-[#f59e0b]/12 px-2.5 py-1 text-xs font-semibold capitalize text-[#b45309]">{e.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="py-10 text-center text-sm text-[#565b73]">Queue is empty</p>}
            </Card>

            <Card title="Cancelled Bookings & Refunds" right={<button className="text-sm font-semibold text-[#2340b5]">View All</button>}>
              <table className="w-full text-left text-sm">
                <thead><tr className="border-b border-[#12142b]/8 text-[10px] uppercase tracking-wide text-[#565b73]"><th className="pb-2">Rider</th><th className="pb-2">Driver</th><th className="pb-2">Rider Refund</th><th className="pb-2">Driver Comp.</th><th className="pb-2">Status</th><th className="pb-2 text-right">Action</th></tr></thead>
                <tbody>
                  {refunds.items.map((r) => (
                    <tr key={r.id} data-testid={`refund-row-${r.id}`} className="border-b border-[#12142b]/5 last:border-0">
                      <td className="py-2.5"><div className="flex items-center gap-2"><Avatar name={r.rider} /><span className="font-medium text-[#12142b]">{r.rider}</span></div></td>
                      <td className="py-2.5"><div className="flex items-center gap-2"><Avatar name={r.driver} /><span className="truncate text-[#565b73]">{r.driver}</span></div></td>
                      <td className="py-2.5 font-semibold text-[#12142b]">{money(r.refund)}<span className="block text-[10px] font-normal text-[#565b73]">{r.rider_pct ?? ""}% of {money(r.booking_base ?? r.booking_total ?? 0)}</span></td>
                      <td className="py-2.5 text-[#565b73]">{money(r.driver_share ?? r.fee)}<span className="block text-[10px] text-[#565b73]">{r.driver_pct ?? ""}% share</span></td>
                      <td className="py-2.5">
                        <span className="rounded-full px-2.5 py-1 text-xs font-semibold"
                          style={r.status === "auto_cancelled" ? { color: "#565b73", backgroundColor: "#565b7314" }
                            : r.auto_approved ? { color: "#0d9488", backgroundColor: "#0d948814" }
                            : r.status === "refunded" ? { color: "#16a34a", backgroundColor: "#16a34a14" }
                            : r.status === "declined" ? { color: "#e4002b", backgroundColor: "#e4002b14" }
                            : { color: "#b45309", backgroundColor: "#f59e0b14" }}
                          data-testid={`refund-status-${r.id}`}>
                          {r.status === "auto_cancelled" ? "Auto-Cancelled" : r.auto_approved ? "Auto-Approved" : r.status}
                        </span>
                        {r.cancel_reason && <span className="mt-1 block max-w-[180px] text-[10px] leading-tight text-[#565b73]">{r.cancel_reason}</span>}
                      </td>
                      <td className="py-2.5 text-right">
                        {r.status === "processing" ? (
                          <button data-testid={`refund-review-${r.id}`} onClick={() => setRefund(r)} className="rounded-lg bg-[#2340b5]/8 px-3 py-1.5 text-xs font-semibold text-[#2340b5] hover:bg-[#2340b5]/15">Review</button>
                        ) : <span className="text-xs text-[#565b73]/70">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 flex items-center justify-between text-sm text-[#565b73]">
                <span>Showing {(refunds.page - 1) * 10 + 1}–{(refunds.page - 1) * 10 + refunds.items.length} of {refunds.total} bookings</span>
                <div className="flex gap-2">
                  <button disabled={refunds.page <= 1} onClick={() => setRpage(refunds.page - 1)} className="rounded-lg border border-[#12142b]/10 px-3 py-1.5 font-medium disabled:opacity-40" data-testid="refunds-prev">Previous</button>
                  <button disabled={refunds.page >= refunds.pages} onClick={() => setRpage(refunds.page + 1)} className="rounded-lg border border-[#12142b]/10 px-3 py-1.5 font-medium disabled:opacity-40" data-testid="refunds-next">Next</button>
                </div>
              </div>
            </Card>
          </div>

          {/* Right column */}
          <div className="space-y-6">
            <Card title="Driver Earnings Overview" right={<button className="text-sm font-semibold text-[#2340b5]">View Report</button>}>
              {[["Total Paid (This Period)", money(de.total_paid)], ["Total Drivers Paid", de.drivers_paid], ["Average Payout", money(de.avg_payout)]].map(([l, v]) => (
                <div key={l} className="flex justify-between border-b border-[#12142b]/6 py-2.5 text-sm last:border-0"><span className="text-[#565b73]">{l}</span><span className="font-semibold text-[#12142b]">{v}</span></div>
              ))}
            </Card>

            <Card title="Quick Actions">
              <div className="grid grid-cols-3 gap-4">
                {QUICK.map((q) => (
                  <button key={q.action} data-testid={`quick-${q.action}`} onClick={() => quick(q.action)} className="flex flex-col items-center gap-2 rounded-xl p-2 text-center transition-colors hover:bg-[#f4f6fb]">
                    <span className="grid h-11 w-11 place-items-center rounded-full" style={{ backgroundColor: `${q.color}18` }}>
                      <q.icon className="h-5 w-5" strokeWidth={1.75} style={{ color: q.color }} />
                    </span>
                    <span className="text-xs font-semibold leading-tight text-[#12142b]">{q.label}</span>
                  </button>
                ))}
              </div>
            </Card>

            <Card title="Recent Activity" right={<button className="text-sm font-semibold text-[#2340b5]">View All</button>}>
              <div className="space-y-3">
                {data.recent_activity.map((a) => (
                  <div key={a.id} className="border-b border-[#12142b]/6 pb-3 last:border-0 last:pb-0">
                    <p className="text-sm font-semibold text-[#12142b]">{a.title} —</p>
                    <p className="text-xs text-[#565b73]">{a.detail}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>

      <PayoutDrawer payout={payout} onClose={() => setPayout(null)} onDone={() => { load(); }} />
      <RefundDialog refund={refund} onClose={() => setRefund(null)} onDone={() => { loadRefunds(); load(); }} />
      <PayoutsAllDialog open={showAllPayouts} onClose={() => setShowAllPayouts(false)} onChanged={() => { load(); }} />
      <EtransferAllDialog open={showEtransfer} onClose={() => setShowEtransfer(false)} onChanged={() => { load(); }} />
      <PendingTripsAllDialog open={showPending} onClose={() => setShowPending(false)} />
      <AwaitingApprovalDialog open={showAwaiting} onClose={() => setShowAwaiting(false)} />
      <SettleRideDialog trip={settleTrip} onClose={() => setSettleTrip(null)} onDone={() => { load(); }} />
      <DisputeDialog trip={disputeTrip} onClose={() => setDisputeTrip(null)} onResolved={() => { load(); }} />
    </div>
  );
}
